/**
 * `PartiesInvolvedSidebar`
 * 
 * A thin wrapper component (presentation layer) that provides consistent layout and styling for displaying parties
 * involved in various report types (SCN and Reasoned Order) using the `PrimaryReportTabList` component.
 * 
 * SCOPE:
 * This component is intentionally kept logic-free, accepting pre-computed `ReportTabListItem[]` to ensure report-specific
 * business logic (pill variants, status text) remains in the respective report pages. Bridges the gap between reports
 * pages and the generic `PrimaryReportTabList`
 * 
 */

import React from 'react';
import PrimaryReportTabList, { ReportTabListItem } from '../../../components/ui/tabs/PrimaryReportTabList';
import styles from './PartiesInvolvedSidebar.module.css';


interface ReportsSidebarProps {
  items: ReportTabListItem[];
  activeSelection?: string | null;
  onSelectionChange: (id: string) => void;
  title?: string;
  className?: string;
}

const ReportsSidebar: React.FC<ReportsSidebarProps> = ({
  items,
  activeSelection,
  onSelectionChange,
  title = 'Show Cause Notices',
  className = '',
}) => {
  const wrapperClassName = `${styles.wrapper} ${className}`.trim();

  return (
    <div className={wrapperClassName}>
      {title && <h3 className={styles.header}>{title}</h3>}
      <PrimaryReportTabList
        items={items}
        selectedId={activeSelection || undefined}
        onItemClick={onSelectionChange}
      />
    </div>
  );
};


interface PartiesInvolvedSkeletonLoaderProps {
  itemCount?: number;
  className?: string;
}

export const PartiesInvolvedSkeletonLoader: React.FC<PartiesInvolvedSkeletonLoaderProps> = ({
  itemCount = 5,
  className = '',
}) => {
  const wrapperClassName = `${styles.wrapper} ${className}`.trim();

  return (
    <div className={wrapperClassName}>
      <div className={styles.header}>
        <div className={styles.skeletonTitle}></div>
      </div>
      <div className={styles.skeletonList}>
        {Array.from({ length: itemCount }).map((_, index) => (
          <div key={index} className={styles.skeletonItem}>
            <div className={styles.skeletonName}></div>
            <div className={styles.skeletonPill}></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportsSidebar;